import React, { Component, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import { Classes, Popover, Position } from '@blueprintjs/core';
import AdminTable from 'app/components/admin/AdminTable';
import { Box, Button, Flex, FlexColumn, Heading, Select, Spinner, Tag, Text, Tooltip } from 'core/components';
import Paginator from 'core/components/Paginator';
import { dateTimeFormatter, formatDateTime } from 'core/util/dateUtils';
import { CELL_TYPES, NewButton } from 'core/components/table';
import { FiRefreshCcw } from 'react-icons/fi';
import storeLoader from 'app/stores/storeLoader';
import RemoveButton from 'core/form/components/RemoveButton';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';
import AlertInvestigationFormDialog from './AutoInvestigationFormDialog';
import SessionsPopover from './SessionsPopover';

const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function RemoveWithRunbookPopover({ model, onRemove, hasRunbookMappings }) {
  const [isOpen, setIsOpen] = useState(false);
  if (!hasRunbookMappings) {
    return (
      <RemoveButton
        model={model}
        onRemove={() => onRemove(false)}
        hideText
        showIcon
        small
        minimal
        text="Remove"
        icon="trash"
        buttonProps={{ title: 'Remove', ariaLabel: 'Remove' }}
      />
    );
  }

  const handleConfirm = (deleteRunbookMappings) => {
    setIsOpen(false);
    onRemove(deleteRunbookMappings);
  };

  return (
    <Popover
      position={Position.BOTTOM_RIGHT}
      isOpen={isOpen}
      onInteraction={setIsOpen}
      content={
        <FlexColumn className={Classes.POPOVER_CONTENT} p={2} onClick={(e) => e.stopPropagation()} maxWidth={330}>
          <Text fontWeight={500} mb={1}>
            Remove the associated runbook mappings?
          </Text>
          <Text muted small mb={2}>
            Keeping them allows you to use them for manual investigations on this policy.
          </Text>

          <Flex alignItems="center" justifyContent="flex-end" gap={1}>
            <Button small minimal onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button small intent="none" onClick={() => handleConfirm(false)}>
              Keep Mappings
            </Button>
            <Button small intent="danger" onClick={() => handleConfirm(true)}>
              Remove Mappings
            </Button>
          </Flex>
        </FlexColumn>
      }
    >
      <Button icon="trash" minimal small intent="danger" title="Remove" ariaLabel="Remove" />
    </Popover>
  );
}

@inject('$notifications', '$kaizen', '$runbook')
@storeLoader('$investigationConfig')
@withTheme
@observer
class AutoInvestigationTable extends Component {
  state = {
    isConfigDialogOpen: false,
    selectedConfig: undefined,
    isSessionsPopoverOpen: false
  };

  autoRefreshInterval = null;

  componentDidMount() {
    this.startAutoRefresh();
  }

  componentWillUnmount() {
    this.stopAutoRefresh();
  }

  get columns() {
    const { $kaizen, $runbook } = this.props;

    const cellActions = {
      type: CELL_TYPES.ACTION,
      actions: [
        (model) => (
          <RemoveWithRunbookPopover
            key="remove"
            hasRunbookMappings={(model.get('runbook_ids') || []).length > 0}
            onRemove={(deleteRunbookMappings) => {
              const destroy = model.destroy({
                remove: true,
                data: { delete_runbook_mappings: deleteRunbookMappings }
              });
              if (deleteRunbookMappings) {
                destroy.then(() => $runbook.collection.fetch({ force: true }));
              }
            }}
          />
        ),
        (model) => <Button key="edit" icon="edit" onClick={() => this.handleConfigure(model)} minimal small />
      ]
    };

    return [
      cellActions,
      {
        label: 'Status',
        name: 'enabled',
        flexBasis: 10,
        renderer: ({ model }) => {
          const enabled = model.get('enabled');
          return (
            <Tag fill textAlign="center" intent={enabled ? 'success' : 'none'}>
              {enabled ? 'Enabled' : 'Disabled'}
            </Tag>
          );
        }
      },
      {
        label: 'Policy Name',
        name: 'policy.name',
        flexBasis: 25,
        computed: true,
        renderer: ({ model }) => {
          const policy = model.get('policy');
          return policy ? policy.name : '-';
        }
      },
      {
        label: 'Investigation Delay',
        name: 'delay_minutes',
        flexBasis: 15,
        computed: true,
        renderer: ({ model }) => {
          const delay = model.get('delay_minutes');
          return delay != null ? `${delay} min` : '—';
        }
      },
      {
        label: 'Sessions',
        name: 'investigations',
        flexBasis: 15,
        renderer: ({ model }) => {
          const investigations = model.get('investigations') || [];
          if (investigations.length === 0) {
            return <Text muted>None</Text>;
          }

          const sessionsData = investigations
            .map((investigation) => {
              const session = $kaizen.chatSessionCollection.get(investigation.session_id);
              return {
                session_id: investigation.session_id,
                title: session
                  ? session.get('user_title') || session.get('session_title') || investigation.session_id
                  : investigation.session_id,
                session,
                investigation
              };
            })
            .filter(Boolean);

          return (
            <SessionsPopover
              sessionsData={sessionsData}
              onOpenChange={this.handleSessionsPopoverOpenChange}
              onSelectSession={(sessionId) => {
                $kaizen.openSessionInCopilot(sessionId);
              }}
            />
          );
        }
      },
      {
        label: 'Runbooks',
        name: 'runbook_ids.length',
        flexBasis: 15
      },
      {
        label: 'Notification Channels',
        name: 'notification_channel_ids.length',
        flexBasis: 15
      },
      {
        label: 'Last Modified',
        name: 'lastModified',
        renderer: dateTimeFormatter,
        computed: true,
        flexBasis: 15
      }
    ];
  }

  stopAutoRefresh = () => {
    if (!this.autoRefreshInterval) {
      return;
    }

    clearInterval(this.autoRefreshInterval);
    this.autoRefreshInterval = null;
  };

  handleRefresh = () => {
    const { $investigationConfig } = this.props;
    const { collection } = $investigationConfig;

    if (!collection || collection.requestStatus === 'fetching') {
      return Promise.resolve();
    }

    const { currentPage = 1, perPage = 20 } = collection.pageState || {};

    return collection.fetch({
      offset: (currentPage - 1) * perPage,
      limit: perPage,
      force: true
    });
  };

  get pagination() {
    const { $investigationConfig, theme } = this.props;
    const { collection } = $investigationConfig;
    const { currentPage, perPage, pageCount, totalCount } = collection.pageState;

    if (totalCount === 0) {
      return null;
    }

    return (
      <Flex
        p="0 16px"
        bg={theme.backgrounds.tableHeader}
        borderTop="thin"
        display="inline-flex"
        alignItems="center"
        justifyContent="flex-end"
        minHeight={40}
      >
        <Text fontWeight={500} fontSize={12} display="inline-flex">
          {collection.requestStatus === 'fetching' ? (
            <Box display="inline-flex" mr={1}>
              <Spinner size={16} mx={1} />
              <Text muted>Loading...</Text>
            </Box>
          ) : (
            <>
              {collection.lastFetched ? (
                <Box display="inline-flex" mr={1}>
                  <Text muted>{`Updated ${formatDateTime(collection.lastFetched, 'HH:mm:ss')}`}</Text>
                </Box>
              ) : null}
              Showing {(currentPage - 1) * perPage + 1} - {Math.min(currentPage * perPage, totalCount)} of {totalCount}
            </>
          )}
        </Text>
        <Flex ml={2} alignItems="center" justifyContent="center">
          <Paginator
            hideFirstAndLastPageLinks
            currentPage={currentPage}
            totalPages={pageCount}
            onChange={this.handlePageChange}
          />
          <Box ml={1}>
            <Select
              height={30}
              menuWidth={110}
              onChange={this.handlePerPageChange}
              options={[
                { label: '10 Per Page', value: 10 },
                { label: '20 Per Page', value: 20 },
                { label: '50 Per Page', value: 50 },
                { label: '100 Per Page', value: 100 }
              ]}
              renderAsButton
              small
              toggle
              values={perPage}
              width={110}
            />
          </Box>
        </Flex>
      </Flex>
    );
  }

  startAutoRefresh = () => {
    const { isSessionsPopoverOpen } = this.state;
    if (this.autoRefreshInterval) {
      return;
    }

    if (isSessionsPopoverOpen) {
      return;
    }

    this.autoRefreshInterval = setInterval(this.handleRefresh, AUTO_REFRESH_INTERVAL_MS);
  };

  handleAdd = () => {
    const { $investigationConfig } = this.props;
    this.setState({ isConfigDialogOpen: true, selectedConfig: $investigationConfig.collection.forge() });
  };

  handleConfigure = (config) => {
    this.setState({ isConfigDialogOpen: true, selectedConfig: config });
  };

  handleCloseDialog = () => {
    this.setState({
      isConfigDialogOpen: false,
      selectedConfig: undefined
    });
  };

  handleSaveConfig = (form, values) => {
    const { $investigationConfig } = this.props;
    const { selectedConfig } = this.state;
    const ruleId = values?.rule_id;

    if (!ruleId) {
      return Promise.reject(new Error('Missing rule_id for policy'));
    }

    return selectedConfig.save(values).then(() => $investigationConfig.collection.fetch({ force: true }));
  };

  handlePageChange = (page) => {
    const { $investigationConfig } = this.props;
    const { collection } = $investigationConfig;
    const { perPage } = collection.pageState;

    collection.setPage(page);
    collection.fetch({
      offset: (page - 1) * perPage,
      limit: perPage,
      force: true
    });
  };

  handlePerPageChange = (value) => {
    const { $investigationConfig } = this.props;
    const { collection } = $investigationConfig;

    collection.setPageState({ perPage: value });
    collection.setPage(1);
    collection.fetch({
      offset: 0,
      limit: value,
      force: true
    });
  };

  handleSessionsPopoverOpenChange = (isSessionsPopoverOpen) => {
    this.setState({ isSessionsPopoverOpen }, () => {
      if (isSessionsPopoverOpen) {
        this.stopAutoRefresh();
      } else {
        this.startAutoRefresh();
      }
    });
  };

  get emptyState() {
    return (
      <FlexColumn alignItems="center" justifyContent="center" height="100%" p={2}>
        <FlexColumn maxWidth="60%" alignItems="flex-start">
          <Heading level={3}>Respond to alerts faster with Auto Investigations</Heading>
          <Text muted mb={1}>
            Configure runbooks to run automatically when alerts fire. Each runbook guides AI Advisor through your
            diagnostic steps, ensuring consistent investigation and faster time to resolution.
          </Text>
          <Text muted>
            Get started by creating your first runbook with the troubleshooting steps for a specific alert scenario.
          </Text>
          <NewButton
            mt={2}
            fill={false}
            text="Configure Auto Investigation"
            onClick={this.handleAdd}
            showIcon={false}
          />
        </FlexColumn>
      </FlexColumn>
    );
  }

  render() {
    const { isConfigDialogOpen, selectedConfig } = this.state;
    const { $investigationConfig, loading: storeLoading } = this.props;
    const { collection } = $investigationConfig;

    if (storeLoading) {
      return (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <Spinner size={40} />
        </Flex>
      );
    }

    if (collection.size === 0) {
      return (
        <>
          {this.emptyState}
          {isConfigDialogOpen && (
            <AlertInvestigationFormDialog
              isOpen={isConfigDialogOpen}
              selectedConfig={selectedConfig}
              onClose={this.handleCloseDialog}
              onSave={this.handleSaveConfig}
            />
          )}
        </>
      );
    }

    return (
      <FlexColumn height="100%" p={4} flex={1}>
        <FlexColumn>
          <SettingsPanelHeading level={2} mb={1}>
            Auto Investigation
          </SettingsPanelHeading>
          <Text>Configure policies to automatically investigate alerts using AI Advisor and runbooks.</Text>
        </FlexColumn>
        <Flex justifyContent="flex-end" mb={2} gap={1}>
          <Tooltip content="Refresh" position={Position.BOTTOM}>
            <Button
              icon={FiRefreshCcw}
              onClick={this.handleRefresh}
              disabled={collection.requestStatus === 'fetching'}
            />
          </Tooltip>
          <NewButton entityName="Configuration" showIcon intent="none" onClick={this.handleAdd} />
        </Flex>

        <AdminTable
          collection={$investigationConfig.collection}
          columns={this.columns}
          fetchCollectionOnMount={false}
          searchBar={false}
          hideResultCount
          // tabPrefId="autoInvestigations"
          flexed
          footer={this.pagination}
        />

        {isConfigDialogOpen && (
          <AlertInvestigationFormDialog
            isOpen={isConfigDialogOpen}
            selectedConfig={selectedConfig}
            onClose={this.handleCloseDialog}
            onSave={this.handleSaveConfig}
          />
        )}
      </FlexColumn>
    );
  }
}

export default AutoInvestigationTable;
